'use client';

import type { PropsWithChildren, AnchorHTMLAttributes, MouseEvent } from 'react';
import Link from 'next/link';

import {
  getLinkType,
  LinkType,
  removeLanguagePrefix,
  cleanBaseUrl,
  cleanMalformedUrl,
  isMailto,
  mapToAppRoute,
  isInternalLink
} from '@/server/smart-link/utils/linkUtils';
import { getBaseUrl } from '@/lib/utils/getBaseUrl';

type SmartLinkProps = PropsWithChildren<{
  href: string;
  prefetch?: boolean;
  sourceUrl?: string;
} & Pick<AnchorHTMLAttributes<HTMLAnchorElement>,
  'className' | 'title' | 'aria-label' | 'aria-describedby' | 'hrefLang' | 'type' | 'download' | 'rel' | 'target' | 'onClick' | 'onMouseEnter' | 'onMouseLeave' | 'onFocus' | 'onBlur'
>>;


function SmartLink(props: SmartLinkProps) {
  const { href, children, prefetch = false, className, title, ['aria-label']: ariaLabel, ['aria-describedby']: ariaDescribedBy, hrefLang, type, download, rel, target, onClick, onMouseEnter, onMouseLeave, onFocus, onBlur } = props;
  
  // Query-only URLs (e.g. ?popup=...) — update URL without page reload
  if (href?.startsWith('?')) {
    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      const newUrl = `${window.location.pathname}${href}`;
      window.history.pushState({}, '', newUrl);
      window.dispatchEvent(new PopStateEvent('popstate'));
      onClick?.(e);
    };
    return (
      <a
        href={href}
        className={className}
        title={title}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        hrefLang={hrefLang}
        type={type}
        download={download}
        rel={rel}
        target={target}
        onClick={handleClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {children}
      </a>
    );
  }

  // Clean base URL, fix malformed URLs, and remove language prefix
  const cleanedHref = cleanBaseUrl(href || '');
  const fixedUrl = cleanMalformedUrl(cleanedHref);
  const hrefWithoutLanguagePrefix = removeLanguagePrefix(fixedUrl);

  // Mailto links: keep as plain anchors without target manipulation
  if (isMailto(hrefWithoutLanguagePrefix)) {
    return (
      <a
        href={hrefWithoutLanguagePrefix}
        className={className}
        title={title}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        hrefLang={hrefLang}
        type={type}
        download={download}
        rel={rel}
        target={target}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {children}
      </a>
    );
  }

  const linkType = getLinkType(hrefWithoutLanguagePrefix);

  if (linkType === LinkType.AFFILIATE) {
    const affiliateSlug = hrefWithoutLanguagePrefix.replace('/go/', '').replace('/affiliate-link/', '');
    return (
      <a
        href={`/go/${affiliateSlug}`}
        className={className}
        target="_blank"
        rel="sponsored nofollow"
        title={title}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        hrefLang={hrefLang}
        type={type}
        download={download}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {children}
      </a>
    );
  }

  if (linkType === LinkType.EXTERNAL) {
    return (
      <a
        href={hrefWithoutLanguagePrefix}
        className={className}
        target="_blank"
        rel={`${rel ? rel : ""} ${rel?.includes("noopener") ? "" : "noopener"}`}
        title={title}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        hrefLang={hrefLang}
        type={type}
        download={download}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {children}
      </a>
    );
  }

  if (isInternalLink(hrefWithoutLanguagePrefix)) {
    const url = new URL(hrefWithoutLanguagePrefix, getBaseUrl());
    const appHref = mapToAppRoute(url.pathname);
    // Preserve query parameters and fragment (hash) if they exist
    const finalHref = `${appHref}${url.search}${url.hash}`;
    return (
      <Link
        href={finalHref}
        className={className}
        title={title}
        target={target || '_self'}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        hrefLang={hrefLang}
        type={type}
        download={download}
        prefetch={prefetch}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {children}
      </Link>
    );
  }

  return (
    <a
      href={hrefWithoutLanguagePrefix}
      className={className}
      target={target || '_self'}
      rel={rel}
      title={title}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      hrefLang={hrefLang}
      type={type}
      download={download}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {children}
    </a>
  );
}

export { SmartLink };
export default SmartLink;


